import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import {
  ILoadedEventArgs,
  ChartTheme,
  ChartComponent,
  ITooltipRenderEventArgs,
} from "@syncfusion/ej2-angular-charts";
import { DropDownList } from "@syncfusion/ej2-angular-dropdowns";
import ResizeObserver from "resize-observer-polyfill";
import { DashboardService } from "../dashboard.service";
import { Subscription } from "rxjs";
import { DecimalPipe } from "@angular/common";
import { debounce } from "./utils";

@Component({
  selector: "rs-bar-charts",
  templateUrl: "./bar-charts.component.html",
  styleUrls: ["./bar-charts.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [DecimalPipe],
})
export class BarChartsComponent implements OnInit {
  @ViewChild("chartContent", { static: false }) content: ElementRef;
  @ViewChild("chart", { static: false }) chart: ChartComponent;
  @Input() dataColumn: any = []; //  柱状图数据源
  @Input() dataLine: any = []; //  折线图数据源
  @Input() dataStackingColumn: any = []; //  堆积图数据源
  @Input() columnType: any = "number";
  @Input() lineType: any = "number";
  @Input() themeColor: any = null;
  @Input() portlet: any = null;
  @Input() dashboard: any = null;
  public column: any[] = [];
  public columnLine: any[] = [];
  public config;
  public _dataStackingColumn = [];
  public marker;
  public specialKey = ["IRR", "DPI", "MOIC", "PIC", "RVPI", "TVPI"];
  pageLoading = false;

  public zoomSettings: Object = {
    // enableMouseWheelZooming: true,
    // enableSelectionZooming: true,
    // toolbarItems: [],
    // enablePan: true,
    enable: false,
  };

  //Initializing Primary X Axis
  public primaryXAxis: Object = {
    valueType: "Category",
    labelIntersectAction: "Rotate45",
    majorGridLines: { width: 0 },
    majorTickLines: { width: 0 },
    edgeLabelPlacement: "Shift",

    // enableScrollbar: true,
    // enableTrim: true,
    // enablePan: true,
    // rotate: 0,
    labelStyle: {
      fontWeight: "normal",
      color: "#44566c",
      fontFamily: "Arial",
      size: "13px",
    },
  };

  //Initializing Primary Y Axis
  public primaryYAxis: Object = {
    title: "", //First Y
    lineStyle: { width: 0 },
    majorTickLines: { width: 0 },
    majorGridLines: { color: "#f2f2f2" }, // 设置主要网格线颜色
    labelFormat: "n2",
  };

  public chartArea: Object = {
    border: {
      width: 0,
    },
  };

  // 鼠标移到bar上面显示的box
  public tooltip: Object = {
    enable: true,
  };

  // 图例设置
  public legend: Object = {
    visible: true,
    enableHighlight: true,
  };
  // custom code start
  public load(args: ILoadedEventArgs): void {
    let selectedTheme: string = location.hash.split("/")[1];
    selectedTheme = selectedTheme ? selectedTheme : "Material";
    args.chart.theme = <ChartTheme>(
      (selectedTheme.charAt(0).toUpperCase() + selectedTheme.slice(1))
        .replace(/-dark/i, "Dark")
        .replace(/contrast/i, "Contrast")
    );
  }
  // custom code end
  public title: string = "";

  height: string = "250px";
  width: string = "100%";

  public customPalette: string[] = []; //默认颜色组
  private subscriptions: Subscription[] = [];
  public exportType: DropDownList;
  public markerFormat = "";

  constructor(
    private ref: ChangeDetectorRef,
    public dashboardService: DashboardService,
    private decimalPipe: DecimalPipe
  ) {}

  ngOnInit() {
    this.config = this.portlet.config && JSON.parse(this.portlet.config);
    this.initMarkerConfig();
    this.initData();
    this.subscriptions.push(
      this.dashboardService.getSwitchConfig().subscribe((res: any) => {
        if (this.portlet.newId === res.id) {
          this.config.hasMarker = res.checked;
          this.portlet.config = JSON.stringify(this.config);
          this.initMarkerConfig();
          this.chart.refresh();
        }
      }),
      this.dashboardService.getMarkerFormat().subscribe((res: any) => {
        if (this.portlet.newId === res.newId) {
          this.config.markerFormat = res.markerFormat;
          this.portlet.config = JSON.stringify(this.config);
          this.markerFormat = res.markerFormat;
          this.initMarkerConfig();
          this.chart.refresh();
        }
      })
    );
  }

  private initMarkerConfig() {
    const format = this.config.portletType === "historicalIRR" ? "p2" : "n2";
    this.pageLoading = false;
    const hasMarker = this.config && this.config.hasMarker;
    this.markerFormat = this.config && this.config.markerFormat;
    this.marker = {
      visible: hasMarker,
      dataLabel: {
        visible: hasMarker,
        format,
      },
    };
  }
  textRender(e) {
    this.dataLine.forEach((data) => {
      Object.keys(data).forEach((key) => {
        if (this.specialKey.includes(key)) {
          if (!e.text) {
            return;
          }
          if (e.text != data[key]) {
            return;
          }
          if (key === "IRR") {
            e.text = (e.text * 100).toFixed(2) + "%";
          } else {
            e.text = Number(e.text).toFixed(2) + "x";
          }
        }
      });
    });
    this.dataColumn.forEach((data) => {
      Object.keys(data).forEach((key) => {
        if (this.specialKey.includes(key)) {
          if (!e.text) {
            return;
          }
          if (e.text != data[key]) {
            return;
          }

          if (key === "IRR") {
            e.text = (e.text * 100).toFixed(2) + "%";
          } else {
            e.text = Number(e.text).toFixed(2) + "x";
          }
        }
      });
    });
    if (!isNaN(e.text)) {
      if (e.text !== 0) {
        switch (this.markerFormat) {
          case "M":
            e.text =
              this.decimalPipe.transform(e.text / 1000000, "1.2-2") + "m";
            return;
          case "K":
            e.text = this.decimalPipe.transform(e.text / 1000, "1.2-2") + "k";
            return;
          case "bn":
            e.text =
              this.decimalPipe.transform(e.text / 1000000000, "1.2-2") + "b";
            return;
          case "T":
            e.text =
              this.decimalPipe.transform(e.text / 1000000000000, "1.2-2") + "t";
            return;
          default:
            return;
        }
      } else {
        e.text = "";
      }
    }
  }

  initData() {
    this.column = [];
    this.columnLine = [];
    if (this.dataColumn && this.dataColumn.length > 0) {
      for (var i in this.dataColumn[0]) {
        if (i != "period") {
          this.column.push({
            xName: "period",
            yName: i,
            name: i,
          });
        }
      }
    }

    if (this.dataLine && this.dataLine.length > 0) {
      for (var i in this.dataLine[0]) {
        if (i != "period") {
          this.columnLine.push({
            xName: "period",
            yName: i,
            name: i,
          });
        }
      }
    }

    if (this.themeColor && this.themeColor.length > 0) {
      this.customPalette = this.themeColor;
    }
    this.ref.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    // if (
    // 	(changes.dataColumn &&
    // 		!this.arraysAreEqual(
    // 			changes.dataColumn.currentValue,
    // 			changes.dataColumn.previousValue
    // 		)) ||
    // 	(changes.dataLine &&
    // 		!this.arraysAreEqual(
    // 			changes.dataLine.currentValue,
    // 			changes.dataLine.previousValue
    // 		)) ||
    // 	!this.arraysAreEqual(
    // 		changes.themeColor ? changes.themeColor.currentValue : null,
    // 		changes.themeColor ? changes.themeColor.previousValue : null
    // 	)
    // ) {
    // 	this.initData();
    // }
  }

  refreshChart() {
    setTimeout(() => {
      if (this.chart) this.chart.refresh();
    }, 0);
  }

  resizeObserver: any;
  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        // 在这里执行你想要的操作，比如更新图表或其他相关操作
        this.changeSize(width, height);
      }
    });
    if (this.content) {
      this.resizeObserver.observe(this.content.nativeElement);
    }
    this.subscriptions.push(
      this.dashboardService.getStartRefresh().subscribe((dashboardId) => {
        if (dashboardId === this.dashboard.id) {
          this.pageLoading = true;
          this.ref.markForCheck();
        }
      })
    );

    this.subscriptions.push(
      this.dashboardService.getRefreshGadget().subscribe((e) => {
        if (!e) {
          return;
        }
        const newId = e["newId"];
        if (newId === this.portlet.newId || newId === this.portlet.id) {
          this.initMarkerConfig();
          this.refreshChart();
        }
      })
    );
  }

  ngOnDestroy() {
    // 在组件销毁时取消监听
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.subscriptions.forEach((ele) => ele.unsubscribe());
  }

  @debounce(100)
  changeSize(width, height) {
    if (!width || !height) {
      return;
    }
    this.width = `${width}px`;
    this.height = `${height - 10}px`;
    this.ref.markForCheck();
  }

  exportChart() {
    this.chart.exportModule.export("PNG", "Chart");
  }

  OnChartAxisLabeRender(args: any) {
    if (args.axis.propName == "primaryYAxis") {
      if (
        this.columnType == "percent" ||
        (this.dataColumn &&
          this.dataColumn.length <= 0 &&
          this.lineType == "percent")
      ) {
        args.text = this.formatPercentage(args.text);
      } else {
        if (isNaN(args.text)) {
          return;
        }
        args.text =
          Number(args.text) >= 0
            ? this.getT(args.text)
            : `-${this.getT(args.text)}`;
      }
    } else if (args.axis.propName == "yAxis2") {
      if (
        this.lineType == "percent" &&
        this.dataColumn &&
        this.dataColumn.length > 0
      ) {
        args.text = this.formatPercentage(args.text);
      } else {
        if (isNaN(args.text)) {
          return;
        }
        args.text =
          Number(args.text) >= 0
            ? this.getT(args.text)
            : `-${this.getT(args.text)}`;
      }
    }
  }

  getT(value) {
    return String(value)
      .replace(/[^0-9.]/g, "")
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    // return this.decimalPipe.transform(Number(value), "1.2-2");
  }

  formatPercentage(decimal) {
    const percentage = (decimal * 100).toFixed(2);
    return `${percentage}%`;
  }

  arraysAreEqual(arr1, arr2) {
    if ((!arr1 && arr2) || (arr1 && !arr2)) {
      return false;
    }

    if (!arr1 && !arr2) {
      return true;
    }

    if (arr1.length !== arr2.length) {
      return false;
    }

    for (let i = 0; i < arr1.length; i++) {
      const obj1 = arr1[i];
      const obj2 = arr2[i];

      if (typeof obj1 !== "object" || typeof obj2 !== "object") {
        if (obj1 !== obj2) {
          return false;
        }
      } else {
        if (!this.objectsAreEqual(obj1, obj2)) {
          return false;
        }
      }
    }

    return true;
  }

  objectsAreEqual(obj1, obj2) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    return true;
  }

  onPointRender(e) {
    if (e.point.y == 0) {
      e.cancel = true;
    }
  }

  public tooltipRender(args: ITooltipRenderEventArgs | any): void {
    if (isNaN(args.data.pointY)) {
      return;
    }
    if (this.config.portletType === "historicalIRR") {
      args.text = `${args.data.pointX}: ${(args.data.pointY * 100).toFixed(
        2
      )}%`;
      return;
    }
    args.text = `${args.data.pointX}: ${this.decimalPipe.transform(
      args.data.pointY,
      "1.2-2"
    )}`;
  }
}
